import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { newHireJourneyKeys, useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { Path, PathDetails, PathId } from "features/Path/models";
import { NewHireFormValues } from "features/PendingNewHire/Form/definition";
import { emailTemplateKeys } from "features/Resources/EmailTemplate/hooks";
import { messageTemplateKeys } from "features/Resources/MessageTemplate/hooks";
import { sharedTemplateKeys } from "features/Resources/SharedTemplate/hooks";
import { TemplatePrefixId } from "features/Resources/SharedTemplate/models";
import { taskTemplateKeys } from "features/Resources/TaskTemplate/hooks";
import { sharedResourceKeys } from "features/Resources/Shared/hooks";
import { meetingKeys } from "features/Resources/Meeting/hooks";
import { updateQueryClientData } from "helpers/query";
import {
  apiDuplicatePathPathById,
  apiNewHirePathsPathByNewHireId,
  apiAddNewHireToPathPathByPathId,
  v2ApiPathPathById,
  apiAddTemplateToPathPathById,
  apiRemoveTemplateFromPathPathById,
} from "helpers/url/api/v2";
import { NewHireJourney, NewHireJourneyId } from "models/v2/newHireJourney";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";

export const pathKeys = {
  all: ["paths"] as const,
  lists: () => [...pathKeys.all, "list"] as const,
  list: (filters: string) => [...pathKeys.lists(), { filters }] as const,
  details: () => [...pathKeys.all, "detail"] as const,
  detail: (id: string = "") => [...pathKeys.details(), id] as const,
  batchCounts: (ids: string[]) => [...pathKeys.all, "batch-counts", [...ids].sort().join(",")] as const,
  byNewHireJourneys: () => [...pathKeys.all, "newHireJourneys"] as const,
  byNewHireJourney: (id: string) => [...pathKeys.byNewHireJourneys(), id] as const,
};

export const useV2PathDetailQuery = (id: string | undefined, options = {}) => useQuery<Path>({
  queryKey: pathKeys.detail(id),
  queryFn: async () => apiClient.get(v2ApiPathPathById(id)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!id && ((options as { enabled?: boolean }).enabled ?? true),
});

export const useV2PathListQuery = (queryString: string, options = {}) => {
  const query = queryString ? `?${queryString}` : "";
  return useQuery<Path[]>({
    queryKey: queryString ? pathKeys.list(queryString) : pathKeys.lists(),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.paths.base)}${query}`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });
};

export const usePathBatchCountsQuery = (ids: string[]) => useQuery<Record<string, PathDetails>>({
  queryKey: pathKeys.batchCounts(ids),
  queryFn: async () => apiClient.post(API_ROUTES.paths.batchCounts, { ids }),
  enabled: ids.length > 0,
  staleTime: 1000 * 60 * 3,
});

export function useCreatePath({ onSuccess, onClose }: { onSuccess?: (path: Path) => void, onClose?: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    {
      mutationFn: async (data: { name: string; autoAdd: boolean; icon?: string; iconColor?: string }) => apiClient.post<Path>(API_ROUTES.paths.base, data),
      onSuccess: (res: Path) => {
        queryClient.invalidateQueries({ queryKey: pathKeys.lists() });
        onSuccess?.(res);
        onClose?.();
        toast({
          title: "New path created",
          status: "success",
        });
      },
      onError: () => {
        toast({ title: "Error", description: "Failed to create path. Please try again.", status: "error" });
      },
    },
  );
}

export function useDuplicatePath({ originalPathId, onClose }: { originalPathId: PathId | undefined, onClose?: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    {
      mutationFn: async (data: { name: string; autoAdd: boolean; icon?: string; iconColor?: string }) => apiClient.post<Path>(
        generatePath(apiDuplicatePathPathById(originalPathId)),
        data,
      ),
      onSuccess: (res: Path) => {
        queryClient.invalidateQueries({ queryKey: pathKeys.lists() });
        onClose?.();
        toast({
          title: "Path duplicated",
          status: "success",
        });
      },
      onError: () => {
        toast({ title: "Error", description: "Failed to duplicate the path. Please try again.", status: "error" });
      },
    },
  );
}

export function useAddPathsToNewHireJourneyById(newHireJourneyId: string, onClose?: () => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (data: Partial<NewHireFormValues>) => {
      const pathIds = data?.pathIds ? { pathIds: data.pathIds } : {};
      return apiClient.put<NewHireJourney>(apiNewHirePathsPathByNewHireId(newHireJourneyId), {
        ...pathIds,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.detail(newHireJourneyId) });
      queryClient.invalidateQueries({ queryKey: pathKeys.byNewHireJourney(newHireJourneyId) });
      queryClient.invalidateQueries({ queryKey: sharedResourceKeys.byNewHireJourney(newHireJourneyId) });
      queryClient.invalidateQueries({ queryKey: meetingKeys.byNewHireJourney(newHireJourneyId) });
      toast({
        title: `New Hire's Paths updated`,
        status: "success",
      });
      onClose?.();
    },
    onError: (e) => {
      console.error(e);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });
}

export function useAddPathToNewHire(pathId: string, onClose?: () => void) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    {
      mutationFn: async (data: { newHireJourneyId: string }) => apiClient.post<NewHireJourney>(
        apiAddNewHireToPathPathByPathId(pathId),
        { newHireJourneyId: data?.newHireJourneyId },
      ),
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: pathKeys.details() });
        queryClient.invalidateQueries({ queryKey: pathKeys.lists() });
        queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.lists() });
        queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.detail(res?.id) });
        queryClient.invalidateQueries({ queryKey: pathKeys.byNewHireJourney(res?.id) });
        toast({
          title: `Path added to New Hire`,
          status: "success",
        });
        onClose?.();
      },
      onError: () => {
        toast({
          title: "Error",
          description: "There was an error, please try again.",
          status: "error",
        });
      },
    },
  );
}

export function useUpdatePath({ pathId, onClose, silent = false }: { pathId: string, onClose?: () => void, silent?: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    {
      mutationFn: async (data: { name: string; autoAdd: boolean; icon?: string; iconColor?: string }) => apiClient.put<Path>(v2ApiPathPathById(pathId), data),
      onSuccess: (res: Path) => {
        queryClient.invalidateQueries({ queryKey: pathKeys.lists() });
        updateQueryClientData<Path>(queryClient, pathKeys.detail(pathId), res);
        onClose?.();
        if (!silent) {
          toast({
            title: "Path updated",
            status: "success",
          });
        }
      },
      onError: () => {
        toast({ title: "Error", description: "Failed to update path. Please try again.", status: "error" });
      },
    },
  );
}

export function useDeleteV2Path(pathId: string, onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => apiClient.delete<Path>(v2ApiPathPathById(pathId)),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: pathKeys.detail(pathId) });
      queryClient.invalidateQueries({ queryKey: pathKeys.lists() });
      toast({
        title: "Path deleted",
        status: "success",
      });
      onSuccess?.();
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });
}

export const useListPathsByNewHireJourneyId = (newHireJourneyId: NewHireJourneyId) => useNewHireJourneyDetailQuery(newHireJourneyId, {
  select: (newHireJourney: NewHireJourney) => newHireJourney.paths,
  queryKey: pathKeys.byNewHireJourney(newHireJourneyId),
});

export function useRemoveTemplateFromPath({ templatePrefixId, pathId, onClose }: { templatePrefixId: TemplatePrefixId, pathId: string, onClose?: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    {
      mutationFn: async () => apiClient.post<Path>(
        apiRemoveTemplateFromPathPathById(pathId),
        { path: { templatePrefixId } },
      ),
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: pathKeys.details() });
        queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.detail(res?.id) });
        queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
        queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
        queryClient.invalidateQueries({ queryKey: messageTemplateKeys.all });
        queryClient.invalidateQueries({ queryKey: taskTemplateKeys.all });
        toast({
          title: `Template removed from Path`,
          status: "success",
        });
        onClose?.();
      },
      onError: () => {
        toast({
          title: "Error",
          description: "There was an error, please try again.",
          status: "error",
        });
      },
    },
  );
}

export function useAddTemplateToPath({ templatePrefixId, pathId, onClose }: { templatePrefixId: TemplatePrefixId, pathId: string, onClose?: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    {
      mutationFn: async () => apiClient.post<Path>(
        apiAddTemplateToPathPathById(pathId),
        { path: { templatePrefixId } },
      ),
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: pathKeys.details() });
        queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.detail(res?.id) });
        queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
        queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
        queryClient.invalidateQueries({ queryKey: messageTemplateKeys.all });
        queryClient.invalidateQueries({ queryKey: taskTemplateKeys.all });
        toast({
          title: `Template added to Path`,
          status: "success",
        });
        onClose?.();
      },
      onError: () => {
        toast({
          title: "Error",
          description: "There was an error, please try again.",
          status: "error",
        });
      },
    },
  );
}
